<template>
  <div v-if="currentInfo" class="coin-chart-container">
    <!-- 水印层 -->
    <div class="watermark" :style="watermarkStyle"></div>

    <!-- 价格展示区域 -->
    <div class="deal-box" :style="bgStyle">
      <div class="share-btn" @click="shareFn">
        分享
        <van-icon name="share-o" class="shareimg" />
      </div>
      <div class="deal-info">
        <div class="time">{{ todayTime }}</div>
        <div class="priceTitle">实时价格</div>
        <div class="price">
          <span v-if="latestPrice" class="unitName">¥</span>
          {{ latestPrice.toString().split('.')[0] || ' ' }}
          <span v-if="latestPrice.toString().split('.').length > 1">.</span>
          <span class="decimal">{{ latestPrice.toString().split('.')[1] }}</span>
        </div>
      </div>
    </div>

    <!-- 图表标题 -->
    <div class="chartTitle" :style="`background:${currentInfo.color || '#C60506'}`">
      {{ breedItem.breedName }}{{ breedItem.info }}实时价格
    </div>

    <!-- 统计信息 -->
    <div v-if="currentInfo" class="chart-tooltip">
      <div class="tooltipItem">
        <div>发行日：{{ currentInfo.issuingDate }}</div>
        <div>发行量：{{ currentInfo.circulation }}</div>
        <div>发行天：{{ getReleaseStatus() }}</div>
        <div style="font-weight: bold">{{ todayTime.split(' ')[0] }}</div>
      </div>
      <div style="background: rgba(252, 230, 230, 0.6)" class="tooltipItem">
        <div>最新价格：{{ currentInfo.lastestPrice }}元</div>
        <div>昨日最低：{{ currentInfo.yesterdayLowPrice }}元</div>
        <div>
          涨跌：<span :class="currentInfo.changeAmount > 0 ? 'col1' : 'col2'"
            >{{ currentInfo.changeAmount }}元</span
          >
        </div>
        <div>
          涨幅比：<span :class="currentInfo.changePercentage > 0 ? 'col1' : 'col2'"
            >{{ currentInfo.changePercentage }}%</span
          >
        </div>
      </div>
      <div class="tooltipItem">
        <div>历史最低：{{ currentInfo.historicalLowPrice }}元</div>
        <div>历史最高：{{ currentInfo.historicalHighPrice }}元</div>
        <div>
          累计涨幅：<span :class="currentInfo.cumulativeIncrease > 0 ? 'col1' : 'col2'"
            >{{ currentInfo.cumulativeIncrease }}元</span
          >
        </div>
        <div>
          累计涨幅比：<span :class="currentInfo.cumulativeIncreasePercentage > 0 ? 'col1' : 'col2'"
            >{{ currentInfo.cumulativeIncreasePercentage }}%</span
          >
        </div>
      </div>
    </div>

    <!-- 图表标题 -->
    <div class="chartTitle" :style="`background:${currentInfo.color || '#C60506'}`">
      {{ breedItem.breedName }}{{ breedItem.info }}实时走势
    </div>

    <!-- 切换标签 -->
    <div class="chartTabs">
      <div v-for="(item, i) in tabs" :key="i" :class="{ active: index === i }" @click="tabIndex(i)">
        {{ item }}
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-box">
      <div ref="chartContainer" class="chart-canvas"></div>
    </div>

    <div class="share-wrap">
      <div class="one">{{ breedItem.unitName }}</div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { queryPannel } from '@/api/base';

export default {
  name: 'CoinChart',
  props: {
    breedItem: {
      type: Object,
      required: true,
    },
    sectionCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      chartInstance: null,
      index: 0,
      tabs: ['日', '周', '月'],
      currentInfo: {
        issuingDate: '',
        circulation: '',
        lastestPrice: '0',
        yesterdayLowPrice: '0',
        changeAmount: '0',
        changePercentage: '0',
        historicalLowPrice: '0',
        historicalHighPrice: '0',
        cumulativeIncrease: '0',
        cumulativeIncreasePercentage: '0',
        color: '#C60506',
      },
      todayTime: '',
      latestPrice: '',
      chartData: {},
      xAxisDate: [],
      watermarkStyle: {},
    };
  },
  computed: {
    bgStyle() {
      if (this.breedItem.image) {
        return `background:url(${this.breedItem.image}) no-repeat 0 0 / 100% 100%`;
      }
      return 'background: linear-gradient(to bottom, #F3B750, #F25E66)';
    },
  },
  watch: {
    breedItem: {
      handler() {
        this.init();
        this.$nextTick(() => {
          this.loadChartData();
        });
      },
      immediate: false,
    },
  },
  mounted() {
    this.init();
    this.createWatermark();
    this.$nextTick(() => {
      this.initChart();
      this.loadChartData();
    });
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },
  methods: {
    createWatermark() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // 设置画布大小
      canvas.width = 300;
      canvas.height = 600;

      // 设置文字样式
      ctx.font = '16px Arial';
      ctx.fillStyle = '#F3D5D3';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 旋转画布并绘制3个水印
      const spacing = 180; // 每个水印之间的间距
      for (let i = 0; i < 3; i++) {
        ctx.save();
        ctx.translate(canvas.width / 2, spacing / 2 + i * spacing);
        ctx.rotate((-20 * Math.PI) / 180);
        ctx.fillText('公众号 一尘盯盘助手', 0, 0);
        ctx.restore();
      }

      // 生成 base64 图片
      const base64Url = canvas.toDataURL();

      // 设置水印样式
      this.watermarkStyle = {
        backgroundImage: `url(${base64Url})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
      };
    },
    async init() {
      try {
        const params = {
          filters: {
            breedId: this.breedItem.breedId,
          },
          themeEn: 'post',
          sceneEn: 'getBreedPanData',
        };
        const res = await queryPannel(params);
        if (res.code === 0 && res.data && res.data.body && res.data.body.length > 0) {
          this.currentInfo = res.data.body[0];
          this.currentInfo.color = res.data.body[0].color || '#C60506';
          this.currentInfo.issuing_date =
            this.currentInfo.issuingDate?.val || this.currentInfo.issuingDate || '';
          this.currentInfo.issuingDate = this.currentInfo.issuingDate
            ? this.formatShortDate(
                this.currentInfo.issuingDate?.val || this.currentInfo.issuingDate,
              )
            : '';
          this.latestPrice = this.currentInfo.lastestPrice || '0.00';
        } else {
          // 使用默认数据
          this.currentInfo = {
            issuingDate: '2024-11-19',
            circulation: '1000万',
            lastestPrice: '0',
            yesterdayLowPrice: '0',
            changeAmount: '0',
            changePercentage: '0',
            historicalLowPrice: '0',
            historicalHighPrice: '0',
            cumulativeIncrease: '0',
            cumulativeIncreasePercentage: '0',
            color: '#C60506',
          };
          this.latestPrice = '0.00';
        }
        this.todayTime = this.formatDate(new Date());
      } catch (error) {
        console.error('加载品种数据失败:', error);
        this.$toast('加载品种数据失败');
      }
    },
    getReleaseStatus() {
      const releaseDay = this.calculateReleaseDay(this.currentInfo.issuingDate);
      return releaseDay > 0 ? `第${releaseDay}天` : '期货';
    },
    calculateReleaseDay(date) {
      if (!date) return 0;
      const releaseDate = new Date(date);
      const today = new Date();
      const diffTime = today - releaseDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}`;
    },
    initChart() {
      console.log(this.$refs.chartContainer);
      if (this.$refs.chartContainer) {
        this.chartInstance = echarts.init(this.$refs.chartContainer);
      }
    },
    async loadChartData() {
      try {
        const sceneEn = this.getSceneEn(this.index);
        this.timeRange(this.index);
        const params = {
          filters: {
            breedId: this.breedItem.breedId,
          },
          themeEn: 'post',
          sceneEn: sceneEn,
        };

        const res = await queryPannel(params);
        if (res.code === 0 && res.data && res.data.body) {
          this.getServerData(res.data.body);
        } else {
          this.getDayInit();
        }
      } catch (error) {
        console.error('加载图表数据失败:', error);
        this.getDayInit();
      }
    },
    getSceneEn(index) {
      const scenes = ['getBreedDataByDay', 'getBreedDataByWeek', 'getBreedDataByMonth'];
      return scenes[index];
    },
    getLastDefinedIndex(arr) {
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i] !== undefined) {
          return i;
        }
      }
      return -1;
    },
    getServerData(obj) {
      // 过滤掉 undefined 和无效数据
      const validData = Array.isArray(obj)
        ? obj.filter(item => item && item.date !== undefined)
        : [];

      if (validData.length === 0) {
        this.getDayInit();
        return;
      }

      let arr1 = [];
      let arr2 = [];
      if (this.index === 0) {
        this.xAxisDate.map(it => {
          let t = validData.find(item => item.date === it);
          arr1.push(`${it}时`);
          const lastDate = validData[validData.length - 1]?.date;
          if (lastDate !== undefined && lastDate >= it) {
            let kt = this.getLastDefinedIndex(arr2);
            arr2.push(t?.price ?? arr2[kt]);
          } else {
            arr2.push(t?.price);
          }
        });
      }

      let categories = [];
      if (this.index === 0) {
        categories = arr1;
      } else {
        categories = validData.map(it => {
          const date = it.date?.val || it.date;
          return this.formatShortDate(date);
        });
      }

      const values = this.index === 0 ? arr2 : validData.map(it => it.price);
      this.renderChart(categories, values);
    },
    getDayInit() {
      let categories = [];
      if (this.index === 0) {
        // 日视图：显示小时
        categories = this.xAxisDate.map(it => `${it}时`);
      } else {
        // 周/月视图：显示日期
        categories = this.xAxisDate;
      }
      this.renderChart(categories, []);
      this.latestPrice = '';
    },
    timeRange(i) {
      const now = new Date();
      this.todayTime = this.formatDate(now);
      this.xAxisDate = [];

      if (i === 0) {
        // 日：0-24小时
        this.xAxisDate = Array.from({ length: 25 }, (_, k) => k);
      } else if (i === 1) {
        // 周：最近7天
        for (let j = 0; j < 7; j++) {
          const day = new Date(now);
          day.setDate(day.getDate() - j);
          this.xAxisDate.push(this.formatShortDate(day));
        }
      } else {
        // 月：最近30天，每6天一个点
        for (let j = 0; j < 30; j++) {
          if (j % 6 === 0) {
            const day = new Date(now);
            day.setDate(day.getDate() - j);
            this.xAxisDate.push(this.formatShortDate(day));
          }
        }
      }
    },
    renderChart(categories, values) {
      if (!this.chartInstance) return;

      const option = {
        color: ['#FC8452'],
        grid: {
          left: '2%',
          right: '3%',
          bottom: '0%',
          top: '10',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            show: true,
            interval: categories.length > 9 ? 3 : 0,
            color: 'rgb(255, 145, 77)',
            rotate: 30,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#DBDBDB',
            },
          },
          axisTick: {
            show: true,
          },
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: values.length > 0 ? null : 100,
          splitNumber: 5,
          axisLabel: {
            show: true,
            color: '#333',
          },
          splitArea: {
            show: true,
            areaStyle: {
              color: ['#f8f8f8', '#ffffff'],
            },
          },
          axisLine: {
            show: false,
            lineStyle: {
              color: '#C03100',
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e0e0e0',
            },
          },
        },
        series: [
          {
            data: values,
            type: 'line',
            smooth: true,
            lineStyle: {
              width: 3,
            },
            itemStyle: {
              color: '#FC8452',
            },
            showSymbol: values.length > 0,
          },
        ],
      };

      this.chartInstance.setOption(option, true);
    },
    formatShortDate(date) {
      if (!date) return '';
      const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${month}.${day}`;
    },
    tabIndex(i) {
      this.index = i;
      this.chartData = {};
      this.loadChartData();
    },
    shareFn() {
      this.$toast('分享功能暂未实现');
    },
  },
};
</script>

<style lang="less" scoped>
.coin-chart-container {
  padding: 5px 0;
  position: relative;
}

.watermark {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.col1 {
  color: #ff0000;
}

.col2 {
  color: #00b100;
}

.chartTabs {
  width: 100%;
  display: inline-flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 15px;
  box-sizing: border-box;
  font-size: 16px;
  color: #d93500;
  font-weight: 500;

  div {
    padding: 5px 12px;
    font-size: 12px;
    background-color: #f3b750;
    color: #d93500;
    cursor: pointer;

    &:first-child {
      border-top-left-radius: 11px;
      border-bottom-left-radius: 11px;
    }

    &:last-child {
      border-top-right-radius: 11px;
      border-bottom-right-radius: 11px;
    }

    &.active {
      background-color: #fad059;
      color: #d93500;
    }
  }
}

.chart-tooltip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  box-sizing: border-box;
  border-radius: 5px;
  background: #f0f0f0;
  margin: 0 7.5px;

  .tooltipItem {
    flex: 1;
    text-align: left;
    padding: 15px;
    padding-right: 0;
    font-size: 12px;
    box-sizing: border-box;
    line-height: 20px;
  }
}

.deal-box {
  background-size: 100% 100%;
  box-sizing: border-box;
  border-radius: 0 0 10px 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  padding-right: 42px;
  min-height: 170px;

  .share-btn {
    display: flex;
    align-items: center;
    position: absolute;
    color: #fff;
    right: 0;
    top: 0;
    background: #d3111c;
    padding: 4px 12px;
    box-sizing: border-box;
    border-radius: 0 0 0 20px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    .shareimg {
      margin-left: 7px;
      font-size: 14px;
    }
  }
}

.chartTitle {
  margin: 0 7.5px;
  margin-top: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding: 10px 0;
  box-sizing: border-box;
  background: linear-gradient(to bottom, #f25e66, #c60506);
  border-radius: 7px 0 14px 0;
  box-shadow: 0 0 5.5px #d9f2ff;
  font-size: 18px;
  font-weight: 500;
}

.share-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  box-sizing: border-box;
  font-size: 15px;

  .one {
    background-color: #f3b750;
    color: #d93500;
    box-sizing: border-box;
    width: 50px;
    text-align: center;
    padding: 3px 0;
    border-radius: 5px;
  }
}

.deal-info {
  margin-top: 40px;
  color: #bf362e;
  text-align: center;

  .time {
    font-size: 12px;
    color: #d93500;
    margin-bottom: 5px;
    line-height: 12px;
  }

  .priceTitle {
    width: 108px;
    height: 35px;
    font-family: PingFang SC;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffedcf;
    padding: 5px;
    box-sizing: border-box;
    margin-bottom: 5px;
    background: #d3111c;
    border-radius: 5px;
  }

  .price {
    line-height: 40px;
    font-size: 40px;
    color: #d3111c;

    .unitName {
      margin-right: 2px;
      font-size: 20px;
    }

    .decimal {
      font-size: 22px;
      font-weight: bold;
    }
  }
}

.charts-box {
  width: 100%;
  box-sizing: border-box;
  height: 200px;

  .chart-canvas {
    width: 100%;
    height: 100%;
  }
}
</style>
